import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import { useAuth } from '../contexts/AuthContext';
import { useProcessing } from '../contexts/ProcessingContext';
import api from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Upload, Music, Settings as SettingsIcon, Loader2, X, Clock, FileAudio, Edit, LogOut, CreditCard, Info, CheckCircle2, Sparkles, Wand2, Download, Mic2, Scissors, BarChart3, FileText, Coins, ChevronDown, ChevronLeft, ChevronRight, User, Archive, ArchiveRestore, Trash2, Mic, Save, RotateCcw, Search, Calendar, UserCircle, Play, GripVertical, PartyPopper, Plus } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import ProcessingModal from '../components/ProcessingModal';
import CreditConfirmModal from '../components/CreditConfirmModal';
import AnimatedBackground from '../components/AnimatedBackground';
import jsmediatags from 'jsmediatags';
import * as Dialog from '@radix-ui/react-dialog';

// All processing phases that indicate the session is still being processed
const PROCESSING_STATUSES = ['uploading', 'processing', 'separating', 'transcribing', 'detecting', 'finalizing'];

export default function Dashboard() {
  const [sessions, setProjects] = useState([]);
  const [archivedSessions, setArchivedSessions] = useState([]);
  const [archivedExpanded, setArchivedExpanded] = useState(false);
  const [sessionsLoaded, setSessionsLoaded] = useState(false);
  const [archivedSessionsLoaded, setArchivedSessionsLoaded] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [processingProject, setProcessingProject] = useState(null);
  const [sessionFile, setSessionFile] = useState(null);
  const [sessionMetadata, setSessionMetadata] = useState({
    title: '',
    artist: '',
    qualifiers: [],
    artwork: null
  });
  const [sessionLyrics, setSessionLyrics] = useState(null);
  const [lyricsLoading, setLyricsLoading] = useState(false);
  const [lyricsError, setLyricsError] = useState(null);
  const [musixmatchResults, setMusixmatchResults] = useState([]);
  const [selectedTrack, setSelectedTrack] = useState(null);
  const [musixmatchSearching, setMusixmatchSearching] = useState(false);
  const [showManualSearchInput, setShowManualSearchInput] = useState(false);
  const [manualSearchArtist, setManualSearchArtist] = useState('');
  const [manualSearchTitle, setManualSearchTitle] = useState('');
  const [userMinutes, setUserMinutes] = useState(0);
  const [showNoCreditsModal, setShowNoCreditsModal] = useState(false);
  const [packages, setPackages] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [toolsExpanded, setToolsExpanded] = useState(true);
  const [stepsExpanded, setStepsExpanded] = useState(true);
  const [showStepsGuide, setShowStepsGuide] = useState(() => {
    return sessionStorage.getItem('hideStepsGuide') !== 'true';
  });
  const [showUploadCreditModal, setShowUploadCreditModal] = useState(false);
  const [deleteConfirmSession, setDeleteConfirmSession] = useState(null);
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [historyPage, setHistoryPage] = useState(1);
  const [editLyricsSession, setEditLyricsSession] = useState(null);
  const [editedLyrics, setEditedLyrics] = useState('');
  const [originalLyrics, setOriginalLyrics] = useState('');
  const [savingLyrics, setSavingLyrics] = useState(false);
  const [editedTitle, setEditedTitle] = useState('');
  const [editedArtist, setEditedArtist] = useState('');
  const [lyricsSearchResults, setLyricsSearchResults] = useState([]);
  const [lyricsSearchLoading, setLyricsSearchLoading] = useState(false);
  const [selectedLyricsTrack, setSelectedLyricsTrack] = useState(null);
  const [previewLyrics, setPreviewLyrics] = useState('');
  const [loadingPreviewLyrics, setLoadingPreviewLyrics] = useState(false);
  const [enhancingLyrics, setEnhancingLyrics] = useState(false);
  const [sharedSongs, setSharedSongs] = useState([]);
  const [sharedSongsSearch, setSharedSongsSearch] = useState('');
  const [sharedSongsPage, setSharedSongsPage] = useState(1);
  const [sharedSongsTotal, setSharedSongsTotal] = useState(0);
  const [sharedSongsTotalPages, setSharedSongsTotalPages] = useState(0);
  const [sharedSongsLoading, setSharedSongsLoading] = useState(false);
  const [myUploadsOnly, setMyUploadsOnly] = useState(false);
  const [infoSession, setInfoSession] = useState(null);
  const [showScreenNameModal, setShowScreenNameModal] = useState(false);
  const [screenNameInput, setScreenNameInput] = useState('');
  const [savingScreenName, setSavingScreenName] = useState(false);
  const [screenNameError, setScreenNameError] = useState('');
  const [partyName, setPartyName] = useState('');
  const [partyPlaylist, setPartyPlaylist] = useState([]);
  const [partyExpanded, setPartyExpanded] = useState(true);
  const [dragOverParty, setDragOverParty] = useState(false);
  const [draggedPartyIndex, setDraggedPartyIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [savedParties, setSavedParties] = useState([]);
  const [savedPartiesLoading, setSavedPartiesLoading] = useState(false);
  const [editingPartyId, setEditingPartyId] = useState(null);
  const [savingParty, setSavingParty] = useState(false);
  const SHARED_PER_PAGE = 10;
  const HISTORY_PER_PAGE = 10;
  const { user, logout, userProfile, refreshUser} = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const sessionSetRef = useRef(false); // Track if session has been set to prevent duplicates

  useEffect(() => {
    fetchProjects();
    fetchArchivedSessions();
    fetchUserProfile();
    fetchPackages();
    fetchSharedSongs();
    fetchSavedParties();
    const interval = setInterval(fetchProjects, 5000); // Poll every 5s for updates
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (searchParams.get('buy') === 'true') {
      setShowNoCreditsModal(true);
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('buy');
      setSearchParams(newParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (userProfile?.minutes !== undefined) {
      setUserMinutes(userProfile.minutes);
    }
  }, [userProfile]);

  // Show screen name modal if user doesn't have one set
  useEffect(() => {
    if (userProfile?.id && !userProfile?.screen_name) {
      setShowScreenNameModal(true);
    }
  }, [userProfile]);

  useEffect(() => {
    if (!userProfile?.id || !sessionsLoaded || !archivedSessionsLoaded) return;
    const welcomeKey = `hasSeenWelcome:${userProfile.id}`;
    const hasSeenWelcome = localStorage.getItem(welcomeKey);
    
    if (!hasSeenWelcome && sessions.length === 0 && archivedSessions.length === 0 && userMinutes > 0) {
      setShowWelcomeModal(true);
    }
  }, [userProfile, sessions, archivedSessions, userMinutes, sessionsLoaded, archivedSessionsLoaded]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchUserProfile();
        refreshUser();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [refreshUser]);

  // Helper function to strip parentheses and clean text
  const stripParentheses = (text) => {
    if (!text) return text;
    return text.replace(/\s*\(.*?\)\s*/g, ' ').trim();
  };

  // Search MusixMatch when title and artist are available
  useEffect(() => {
    const searchMusixMatch = async () => {
      if (!sessionFile || !sessionMetadata.title || !sessionMetadata.artist) {
        setMusixmatchResults([]);
        setSelectedTrack(null);
        setSessionLyrics(null);
        setLyricsError(null);
        return;
      }
      
      setMusixmatchSearching(true);
      setLyricsError(null);
      
      try {
        const cleanTitle = stripParentheses(sessionMetadata.title);
        const cleanArtist = stripParentheses(sessionMetadata.artist);
        
        const response = await api.get('/musixmatch/search', {
          params: {
            artist: cleanArtist,
            title: cleanTitle
          }
        });
        
        if (response.data.success && response.data.tracks.length > 0) {
          setMusixmatchResults(response.data.tracks);
          setSelectedTrack(response.data.tracks[0]);
          setLyricsError(null);
        } else {
          setMusixmatchResults([]);
          setSelectedTrack(null);
          setLyricsError('No matching songs found');
        }
      } catch (error) {
        console.error('Failed to search MusixMatch:', error);
        setMusixmatchResults([]);
        setSelectedTrack(null);
        setLyricsError('Failed to search for lyrics');
      } finally {
        setMusixmatchSearching(false);
      }
    };
    
    const timeoutId = setTimeout(searchMusixMatch, 500);
    return () => clearTimeout(timeoutId);
  }, [sessionFile, sessionMetadata.title, sessionMetadata.artist]);

  const handleSearchAgain = () => {
    setShowManualSearchInput(true);
    setManualSearchArtist(sessionMetadata.artist);
    setManualSearchTitle(sessionMetadata.title);
    setSelectedTrack(null);
    setMusixmatchResults([]);
    setLyricsError(null);
  };

  const handleManualSearch = async () => {
    if (!manualSearchArtist || !manualSearchTitle) {
      toast.error('Please enter both artist and title');
      return;
    }

    setMusixmatchSearching(true);
    setLyricsError(null);

    try {
      const cleanTitle = stripParentheses(manualSearchTitle);
      const cleanArtist = stripParentheses(manualSearchArtist);

      const response = await api.get('/musixmatch/search', {
        params: {
          artist: cleanArtist,
          title: cleanTitle
        }
      });

      if (response.data.success && response.data.tracks.length > 0) {
        setMusixmatchResults(response.data.tracks);
        setSelectedTrack(response.data.tracks[0]);
      } else {
        setMusixmatchResults([]);
        setSelectedTrack(null);
        setLyricsError('No matching songs found');
      }
    } catch (error) {
      console.error('Failed to search MusixMatch:', error);
      setLyricsError('Failed to search for lyrics');
    } finally {
      setMusixmatchSearching(false);
    }
  };

  const handleConfirmTrack = (track) => {
    setSelectedTrack(track);
    setShowManualSearchInput(false);
  };

  useEffect(() => {
    const toolsTimer = setTimeout(() => {
      setToolsExpanded(false);
    }, 3000);
    const stepsTimer = setTimeout(() => {
      setStepsExpanded(false);
    }, 6000);
    return () => {
      clearTimeout(toolsTimer);
      clearTimeout(stepsTimer);
    };
  }, []);

  const fetchUserProfile = async () => {
    try {
      const response = await api.get('/user/profile');
      setUserMinutes(response.data.minutes || 0);
    } catch (error) {
      console.error('Failed to fetch user profile:', error);
    }
  };

  const fetchPackages = async () => {
    try {
      const response = await api.get('/payments/packages');
      setPackages(response.data.packages || []);
    } catch (error) {
      console.error('Failed to fetch packages:', error);
    }
  };

  const purchaseMinutes = async (minutes) => {
    try {
      setSelectedPackage(minutes);
      setIsProcessingPayment(true);
      
      const originUrl = window.location.origin;
      const response = await api.post('/payments/checkout/session', {
        minutes,
        origin_url: originUrl
      });
      
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initiate payment');
      setIsProcessingPayment(false);
      setSelectedPackage(null);
    }
  };

  // Get background session from ProcessingContext
  const { backgroundSession } = useProcessing();

  // Watch for processing sessions and show modal
  useEffect(() => {
    const activeProject = sessions.find(p => 
      PROCESSING_STATUSES.includes(p.status)
    );
    
    // Don't show modal if the session is running in background mode
    if (activeProject && activeProject.id === backgroundSession) {
      return;
    }
    
    if (activeProject && !processingProject) {
      setProcessingProject(activeProject);
    } else if (activeProject && processingProject) {
      // Update status if changed
      if (activeProject.status !== processingProject.status) {
        setProcessingProject(activeProject);
      }
    } else if (!activeProject && processingProject) {
      // Processing complete
      const completedProject = sessions.find(p => p.id === processingProject.id);
      if (completedProject?.status === 'complete') {
        if (completedProject.no_curses_detected) {
          toast.info('Your karaoke track is ready!');
        } else {
          toast.success('Your karaoke track is ready - time to sing!');
        }
        setTimeout(() => {
          setProcessingProject(null);
          navigate(`/sing/${completedProject.id}`);
        }, 1500);
      } else if (completedProject?.status === 'error') {
        toast.error(completedProject.error_message || 'Processing failed. Please try again.');
        setProcessingProject(null);
      }
    }
  }, [sessions, processingProject, navigate, backgroundSession]);

  const fetchProjects = async () => {
    try {
      const response = await api.get('/sessions?limit=100');
      const sessionsData = response.data.sessions || response.data;
      setProjects(Array.isArray(sessionsData) ? sessionsData : []);
    } catch (error) {
      console.error('Failed to fetch sessions:', error);
    } finally {
      setSessionsLoaded(true);
    }
  };

  const fetchArchivedSessions = async () => {
    try {
      const response = await api.get('/sessions/archived');
      setArchivedSessions(response.data);
    } catch (error) {
      console.error('Failed to fetch archived sessions:', error);
    } finally {
      setArchivedSessionsLoaded(true);
    }
  };

  const fetchSharedSongs = async (search = sharedSongsSearch, page = sharedSongsPage, myOnly = myUploadsOnly) => {
    setSharedSongsLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      params.append('page', page.toString());
      params.append('limit', SHARED_PER_PAGE.toString());
      if (myOnly) params.append('my_uploads', 'true');
      
      const response = await api.get(`/sessions/shared?${params.toString()}`);
      setSharedSongs(response.data.sessions || []);
      setSharedSongsTotal(response.data.total || 0);
      setSharedSongsTotalPages(response.data.total_pages || 0);
    } catch (error) {
      console.error('Failed to fetch shared songs:', error);
    } finally {
      setSharedSongsLoading(false);
    }
  };

  useEffect(() => {
    fetchSharedSongs(sharedSongsSearch, sharedSongsPage, myUploadsOnly);
  }, [sharedSongsSearch, sharedSongsPage, myUploadsOnly]);

  const handleSharedSongsSearch = (e) => {
    setSharedSongsSearch(e.target.value);
    setSharedSongsPage(1);
  };

  const handleMyUploadsToggle = () => {
    setMyUploadsOnly(!myUploadsOnly);
    setSharedSongsPage(1);
  };

  const handleSharedSongsPageChange = (newPage) => {
    if (newPage >= 1 && newPage <= sharedSongsTotalPages) {
      setSharedSongsPage(newPage);
    }
  };

  const handleSongDragStart = (e, session) => {
    e.dataTransfer.setData('application/json', JSON.stringify({
      id: session.id,
      title: session.title || session.filename,
      artist: session.artist || 'Unknown Artist',
      duration: session.duration
    }));
    e.dataTransfer.effectAllowed = 'copy';
  };

  const handlePartyDrop = (e) => {
    e.preventDefault();
    setDragOverParty(false);
    try {
      const songData = JSON.parse(e.dataTransfer.getData('application/json'));
      if (songData.id && !partyPlaylist.find(s => s.id === songData.id)) {
        setPartyPlaylist(prev => [...prev, { ...songData, singer: '' }]);
      }
    } catch (err) {
      console.error('Drop error:', err);
    }
  };

  const handlePartyDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    setDragOverParty(true);
  };

  const handlePartyDragLeave = () => {
    setDragOverParty(false);
  };

  const updatePartySinger = (index, singer) => {
    setPartyPlaylist(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], singer };
      return updated;
    });
  };

  const removeFromParty = (index) => {
    setPartyPlaylist(prev => prev.filter((_, i) => i !== index));
  };

  const handlePartyItemDragStart = (e, index) => {
    setDraggedPartyIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handlePartyItemDragOver = (e, index) => {
    e.preventDefault();
    if (draggedPartyIndex !== null && draggedPartyIndex !== index) {
      setDragOverIndex(index);
    }
  };

  const handlePartyItemDrop = (e, targetIndex) => {
    e.preventDefault();
    if (draggedPartyIndex !== null && draggedPartyIndex !== targetIndex) {
      setPartyPlaylist(prev => {
        const updated = [...prev];
        const [removed] = updated.splice(draggedPartyIndex, 1);
        updated.splice(targetIndex, 0, removed);
        return updated;
      });
    }
    setDraggedPartyIndex(null);
    setDragOverIndex(null);
  };

  const handlePartyItemDragEnd = () => {
    setDraggedPartyIndex(null);
    setDragOverIndex(null);
  };

  const startParty = () => {
    if (partyPlaylist.length === 0) {
      toast.error('Add some songs to your party playlist first!');
      return;
    }
    const partyData = {
      name: partyName || 'Karaoke Party',
      songs: partyPlaylist
    };
    sessionStorage.setItem('partyPlaylist', JSON.stringify(partyData));
    navigate(`/sing/${partyPlaylist[0].id}?party=true`);
  };

  const fetchSavedParties = async () => {
    setSavedPartiesLoading(true);
    try {
      const response = await api.get('/parties');
      setSavedParties(response.data.parties || []);
    } catch (error) {
      console.error('Failed to fetch saved parties:', error);
    } finally {
      setSavedPartiesLoading(false);
    }
  };

  const saveParty = async () => {
    if (partyPlaylist.length === 0) {
      toast.error('Add some songs to your party first!');
      return;
    }
    setSavingParty(true);
    try {
      if (editingPartyId) {
        await api.put(`/parties/${editingPartyId}`, {
          name: partyName || 'Karaoke Party',
          songs: partyPlaylist
        });
        toast.success('Party updated!');
      } else {
        await api.post('/parties', {
          name: partyName || 'Karaoke Party',
          songs: partyPlaylist
        });
        toast.success('Party saved!');
      }
      fetchSavedParties();
      setPartyName('');
      setPartyPlaylist([]);
      setEditingPartyId(null);
    } catch (error) {
      toast.error('Failed to save party');
      console.error('Failed to save party:', error);
    } finally {
      setSavingParty(false);
    }
  };

  const loadParty = (party) => {
    setPartyName(party.name);
    setPartyPlaylist(party.songs || []);
    setEditingPartyId(party.id);
    toast.success(`Loaded "${party.name}"`);
  };

  const deleteParty = async (partyId) => {
    try {
      await api.delete(`/parties/${partyId}`);
      toast.success('Party deleted');
      fetchSavedParties();
      if (editingPartyId === partyId) {
        setEditingPartyId(null);
        setPartyName('');
        setPartyPlaylist([]);
      }
    } catch (error) {
      toast.error('Failed to delete party');
      console.error('Failed to delete party:', error);
    }
  };

  const clearPartyEditor = () => {
    setPartyName('');
    setPartyPlaylist([]);
    setEditingPartyId(null);
  };

  const archiveSession = async (sessionId) => {
    try {
      await api.put(`/sessions/${sessionId}/archive`);
      toast.success('Session archived');
      fetchProjects();
      fetchArchivedSessions();
    } catch (error) {
      toast.error('Failed to archive session');
      console.error('Failed to archive session:', error);
    }
  };

  const restoreSession = async (sessionId) => {
    try {
      await api.put(`/sessions/${sessionId}/restore`);
      toast.success('Session restored');
      fetchProjects();
      fetchArchivedSessions();
    } catch (error) {
      toast.error('Failed to restore session');
      console.error('Failed to restore session:', error);
    }
  };

  const deleteSession = async (sessionId) => {
    try {
      await api.delete(`/sessions/${sessionId}`);
      toast.success('Session deleted permanently');
      setDeleteConfirmSession(null);
      fetchArchivedSessions();
    } catch (error) {
      toast.error('Failed to delete session');
      console.error('Failed to delete session:', error);
    }
  };

  // Get current sessions (complete but not yet processed/exported)
  const currentSessions = sessions.filter(s => 
    s.status === 'complete' && !s.files?.clean_url
  );

  // Get history sessions (processed and exported)
  const allHistorySessions = sessions.filter(s =>
    s.files?.clean_url  // Has been processed and exported
  );
  const totalHistoryPages = Math.ceil(allHistorySessions.length / HISTORY_PER_PAGE);
  const historySessions = allHistorySessions.slice(
    (historyPage - 1) * HISTORY_PER_PAGE,
    historyPage * HISTORY_PER_PAGE
  );

  const handleHistoryPageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalHistoryPages) {
      setHistoryPage(newPage);
    }
  };

  const handleEditSession = (session) => {
    const lyrics = session.custom_lyrics || 
                   session.enhanced?.lyricsText || 
                   session.source_lyrics || 
                   (session.transcription?.words?.map(w => w.word).join(' ')) || 
                   '';
    setEditLyricsSession(session);
    setEditedLyrics(lyrics);
    setOriginalLyrics(session.enhanced?.lyricsText || session.source_lyrics || '');
    setEditedTitle(session.title || '');
    setEditedArtist(session.artist || '');
  };

  const handleSaveLyrics = async () => {
    if (!editLyricsSession) return;
    
    // Validate lyrics length (15k character limit)
    if (editedLyrics.length > 15000) {
      toast.error('Lyrics are too long (max 15,000 characters). Please shorten them.');
      return;
    }
    
    setSavingLyrics(true);
    try {
      // Save lyrics
      await api.put(`/sessions/${editLyricsSession.id}/custom-lyrics`, {
        custom_lyrics: editedLyrics
      });
      // Save title/artist if changed
      const titleChanged = editedTitle !== (editLyricsSession.title || '');
      const artistChanged = editedArtist !== (editLyricsSession.artist || '');
      if (titleChanged || artistChanged) {
        await api.put(`/sessions/${editLyricsSession.id}`, {
          title: editedTitle,
          artist: editedArtist
        });
      }
      toast.success('Changes saved successfully');
      setEditLyricsSession(null);
      fetchProjects();
    } catch (error) {
      toast.error('Failed to save changes');
      console.error('Failed to save:', error);
    } finally {
      setSavingLyrics(false);
    }
  };

  const handleResetLyrics = () => {
    setEditedLyrics(originalLyrics);
  };

  // Search for lyrics using Musixmatch
  const handleSearchLyrics = async () => {
    if (!editedTitle && !editedArtist) {
      toast.error('Please enter a title or artist to search');
      return;
    }
    
    setLyricsSearchLoading(true);
    setLyricsSearchResults([]);
    setSelectedLyricsTrack(null);
    setPreviewLyrics('');
    
    try {
      const params = new URLSearchParams();
      if (editedArtist) params.append('artist', editedArtist);
      if (editedTitle) params.append('title', editedTitle);
      
      const response = await api.get(`/musixmatch/search?${params.toString()}`);
      if (response.data.success && response.data.tracks?.length > 0) {
        setLyricsSearchResults(response.data.tracks);
        toast.success(`Found ${response.data.tracks.length} result(s)`);
      } else {
        toast.info('No lyrics found for this song');
      }
    } catch (error) {
      console.error('Lyrics search error:', error);
      toast.error('Failed to search for lyrics');
    } finally {
      setLyricsSearchLoading(false);
    }
  };

  // Load preview of lyrics from selected track
  const handleSelectLyricsTrack = async (track) => {
    setSelectedLyricsTrack(track);
    setLoadingPreviewLyrics(true);
    
    try {
      const response = await api.get(`/musixmatch/lyrics/${track.commontrack_id}`);
      if (response.data.success && response.data.lyrics) {
        setPreviewLyrics(response.data.lyrics);
      } else {
        toast.error('Could not load lyrics for this track');
        setPreviewLyrics('');
      }
    } catch (error) {
      console.error('Lyrics fetch error:', error);
      toast.error('Failed to load lyrics');
      setPreviewLyrics('');
    } finally {
      setLoadingPreviewLyrics(false);
    }
  };

  // Apply the preview lyrics to the editor
  const handleApplyLyrics = () => {
    if (previewLyrics) {
      setEditedLyrics(previewLyrics);
      setLyricsSearchResults([]);
      setSelectedLyricsTrack(null);
      setPreviewLyrics('');
      toast.success('Lyrics applied! Click "Save & Enhance" to sync with audio.');
    }
  };

  // Enhance lyrics - save and sync with audio
  const handleEnhanceLyrics = async () => {
    if (!editLyricsSession) return;
    if (!editedLyrics.trim()) {
      toast.error('Please add lyrics first');
      return;
    }
    
    // Validate lyrics length (15k character limit)
    if (editedLyrics.length > 15000) {
      toast.error('Lyrics are too long (max 15,000 characters). Please shorten them.');
      return;
    }
    
    setEnhancingLyrics(true);
    try {
      // First save the custom lyrics
      await api.put(`/sessions/${editLyricsSession.id}/custom-lyrics`, {
        custom_lyrics: editedLyrics
      });
      
      // Save title/artist if changed
      const titleChanged = editedTitle !== (editLyricsSession.title || '');
      const artistChanged = editedArtist !== (editLyricsSession.artist || '');
      if (titleChanged || artistChanged) {
        await api.put(`/sessions/${editLyricsSession.id}`, {
          title: editedTitle,
          artist: editedArtist
        });
      }
      
      // Then enhance/sync with audio
      await api.post(`/sessions/${editLyricsSession.id}/enhance-lyrics`, {
        custom_lyrics: editedLyrics
      });
      
      toast.success('Lyrics saved and synced with audio!');
      setEditLyricsSession(null);
      setLyricsSearchResults([]);
      setSelectedLyricsTrack(null);
      setPreviewLyrics('');
      fetchProjects();
      fetchSharedSongs();
    } catch (error) {
      console.error('Enhance error:', error);
      if (error.response?.data?.detail?.includes('No transcription')) {
        toast.error('Audio needs to be processed first before syncing lyrics');
      } else {
        toast.error('Failed to enhance lyrics');
      }
    } finally {
      setEnhancingLyrics(false);
    }
  };

  const handleSaveScreenName = async () => {
    const trimmed = screenNameInput.trim();
    if (!trimmed) {
      setScreenNameError('Please enter a screen name');
      return;
    }
    if (trimmed.length < 2) {
      setScreenNameError('Screen name must be at least 2 characters');
      return;
    }
    if (trimmed.length > 30) {
      setScreenNameError('Screen name must be 30 characters or less');
      return;
    }
    
    setSavingScreenName(true);
    setScreenNameError('');
    try {
      await api.put('/user/profile', { screen_name: trimmed });
      await refreshUser();
      setShowScreenNameModal(false);
      setScreenNameInput('');
      toast.success('Screen name saved!');
    } catch (error) {
      setScreenNameError('Failed to save screen name. Please try again.');
      console.error('Failed to save screen name:', error);
    } finally {
      setSavingScreenName(false);
    }
  };

  const formatTime = (seconds) => {
    if (!seconds) return '-';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${String(secs).padStart(2, '0')}`;
  };

  const cleanTitle = (title) => {
    if (!title) return title;
    return title.replace(/\s*\(Clean\)\s*$/i, '').trim();
  };

  // Helper function to parse filename intelligently
  const parseFilename = (filename) => {
    // Remove extension
    const nameWithoutExt = filename.replace(/\.[^/.]+$/, '');
    
    // Extract qualifiers (Intro, Edit, Remix, etc.) in parentheses
    const qualifiersRegex = /\(([^)]+)\)/g;
    const qualifiers = [];
    let match;
    let baseName = nameWithoutExt;
    
    while ((match = qualifiersRegex.exec(nameWithoutExt)) !== null) {
      qualifiers.push(match[1]);
      baseName = baseName.replace(match[0], '').trim();
    }
    
    // Try to split by common delimiters
    let artist = '';
    let title = baseName;
    
    // Check for " - " delimiter (most common)
    if (baseName.includes(' - ')) {
      const parts = baseName.split(' - ');
      artist = parts[0].trim();
      title = parts.slice(1).join(' - ').trim();
    }
    // Check for " – " (em dash)
    else if (baseName.includes(' – ')) {
      const parts = baseName.split(' – ');
      artist = parts[0].trim();
      title = parts.slice(1).join(' – ').trim();
    }
    
    return {
      artist,
      title: title || baseName,
      qualifiers
    };
  };

  const onDrop = useCallback((acceptedFiles) => {
    const file = acceptedFiles[0];
    if (!file) return;

    // Reset the ref for new file drop
    sessionSetRef.current = false;

    // Get audio duration
    const audio = new Audio();
    const objectUrl = URL.createObjectURL(file);
    audio.src = objectUrl;
    
    // Try to read ID3 tags
    jsmediatags.read(file, {
      onSuccess: (tag) => {
        const tags = tag.tags;
        const parsed = parseFilename(file.name);
        
        // Extract artwork from ID3 tags
        let artworkUrl = null;
        if (tags.picture) {
          const { data, format } = tags.picture;
          const base64String = data.reduce((acc, byte) => acc + String.fromCharCode(byte), '');
          artworkUrl = `data:${format};base64,${btoa(base64String)}`;
        }
        
        // Set session file with duration
        const handleLoadedMetadata = () => {
          // Prevent duplicate calls
          if (sessionSetRef.current) return;
          sessionSetRef.current = true;
          
          // Get file extension without dot
          const ext = file.name.split('.').pop().toUpperCase();
          
          setSessionFile({
            file,
            name: file.name,
            size: file.size,
            type: file.type,
            format: ext, // MP3, WAV, FLAC, etc.
            duration: audio.duration || 0
          });
          
          // Use ID3 tags if available, otherwise use parsed filename
          setSessionMetadata({
            title: tags.title || parsed.title,
            artist: tags.artist || parsed.artist,
            qualifiers: parsed.qualifiers,
            artwork: artworkUrl
          });
          
          URL.revokeObjectURL(objectUrl);
        };
        
        audio.addEventListener('loadedmetadata', handleLoadedMetadata);
        
        // Also try to load immediately if already cached
        if (audio.readyState >= 1) {
          handleLoadedMetadata();
        }
        
        // Fallback if duration doesn't load within 3 seconds
        setTimeout(() => {
          if (!sessionSetRef.current) {
            sessionSetRef.current = true;
            const ext = file.name.split('.').pop().toUpperCase();
            
            setSessionFile({
              file,
              name: file.name,
              size: file.size,
              type: file.type,
              format: ext,
              duration: audio.duration || 0
            });
            setSessionMetadata({
              title: tags.title || parsed.title,
              artist: tags.artist || parsed.artist,
              qualifiers: parsed.qualifiers,
              artwork: artworkUrl
            });
            URL.revokeObjectURL(objectUrl);
          }
        }, 3000);
      },
      onError: (error) => {
        // Fallback to filename parsing if ID3 reading fails
        console.log('ID3 tag reading failed, using filename parsing:', error);
        const parsed = parseFilename(file.name);
        
        const handleLoadedMetadata = () => {
          // Prevent duplicate calls
          if (sessionSetRef.current) return;
          sessionSetRef.current = true;
          
          const ext = file.name.split('.').pop().toUpperCase();
          
          setSessionFile({
            file,
            name: file.name,
            size: file.size,
            type: file.type,
            format: ext,
            duration: audio.duration || 0
          });
          setSessionMetadata({
            title: parsed.title,
            artist: parsed.artist,
            qualifiers: parsed.qualifiers,
            artwork: null
          });
          URL.revokeObjectURL(objectUrl);
        };
        
        audio.addEventListener('loadedmetadata', handleLoadedMetadata);
        
        if (audio.readyState >= 1) {
          handleLoadedMetadata();
        }
        
        setTimeout(() => {
          if (!sessionSetRef.current) {
            sessionSetRef.current = true;
            const ext = file.name.split('.').pop().toUpperCase();
            
            setSessionFile({
              file,
              name: file.name,
              size: file.size,
              type: file.type,
              format: ext,
              duration: 0
            });
            setSessionMetadata({
              title: parsed.title,
              artist: parsed.artist,
              qualifiers: parsed.qualifiers,
              artwork: null
            });
            URL.revokeObjectURL(objectUrl);
          }
        }, 3000);
      }
    });
  }, [sessionFile]);

  const handleUploadAndAnalyze = () => {
    if (!sessionFile) return;
    setShowUploadCreditModal(true);
  };

  const handleUploadConfirmed = async () => {
    if (!sessionFile) return;
    
    setShowUploadCreditModal(false);
    setUploading(true);
    setUploadProgress(0);

    try {
      // Step 0: Fetch lyrics from MusixMatch if a track is selected
      let lyricsToUpload = null;
      if (selectedTrack?.commontrack_id) {
        setLyricsLoading(true);
        try {
          const lyricsResponse = await api.get(`/musixmatch/lyrics/${selectedTrack.commontrack_id}`);
          if (lyricsResponse.data.success) {
            lyricsToUpload = lyricsResponse.data.lyrics;
          }
        } catch (lyricsError) {
          console.error('Failed to fetch lyrics:', lyricsError);
        } finally {
          setLyricsLoading(false);
        }
      }

      const formData = new FormData();
      formData.append('file', sessionFile.file);
      if (sessionMetadata.title) formData.append('title', sessionMetadata.title);
      if (sessionMetadata.artist) formData.append('artist', sessionMetadata.artist);
      if (sessionMetadata.qualifiers && sessionMetadata.qualifiers.length > 0) {
        formData.append('qualifiers', JSON.stringify(sessionMetadata.qualifiers));
      }
      if (lyricsToUpload) {
        formData.append('source_lyrics', lyricsToUpload);
      }

      // Step 1: Upload file
      const uploadResponse = await api.post('/sessions/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        },
      });

      const sessionId = uploadResponse.data.id;
      toast.success('Upload successful! Starting analysis...');
      
      // Step 2: Start analysis
      const analyzeResponse = await api.post(`/sessions/${sessionId}/analyze`);
      
      toast.success('Analysis started! Processing your track.');
      
      // Clear session and set processing session
      setSessionFile(null);
      setSessionMetadata({ title: '', artist: '', qualifiers: [], artwork: null });
      setMusixmatchResults([]);
      setSelectedTrack(null);
      setShowManualSearchInput(false);
      setManualSearchArtist('');
      setManualSearchTitle('');
      setProcessingProject(uploadResponse.data);
      
      fetchProjects();
      fetchUserProfile();
      refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleCancelSession = () => {
    setSessionFile(null);
    setSessionMetadata({ title: '', artist: '', qualifiers: [], artwork: null });
    setMusixmatchResults([]);
    setSelectedTrack(null);
    setShowManualSearchInput(false);
    setManualSearchArtist('');
    setManualSearchTitle('');
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'audio/*': ['.mp3', '.wav', '.m4a', '.aac', '.ogg', '.flac', '.aiff'],
    },
    maxFiles: 1,
    disabled: uploading || !!sessionFile,
  });

  const getStatusBadge = (status) => {
    const variants = {
      uploading: 'default',
      processing: 'secondary',
      separating: 'secondary',
      transcribing: 'secondary',
      detecting: 'secondary',
      finalizing: 'secondary',
      complete: 'default',
      error: 'destructive',
    };

    const colors = {
      uploading: 'bg-blue-500/10 text-blue-500',
      processing: 'bg-yellow-500/10 text-yellow-500',
      separating: 'bg-orange-500/10 text-orange-500',
      transcribing: 'bg-purple-500/10 text-purple-500',
      detecting: 'bg-cyan-500/10 text-cyan-500',
      finalizing: 'bg-indigo-500/10 text-indigo-500',
      complete: 'bg-green-500/10 text-green-500',
      error: 'bg-red-500/10 text-red-500',
    };

    const isProcessing = PROCESSING_STATUSES.includes(status);

    return (
      <Badge className={colors[status] || colors.processing}>
        {isProcessing ? (
          <Loader2 className="h-3 w-3 mr-1 animate-spin" />
        ) : null}
        {status}
      </Badge>
    );
  };

  const handleProjectClick = (session) => {
    if (session.status === 'complete') {
      navigate(`/sing/${session.id}`);
    }
  };

  return (
    <div className="flex-1 relative">
      <AnimatedBackground />
      {/* Main Content */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8 relative z-10">
        {/* What You Can Do Section - Always visible */}
        {!sessionFile && showStepsGuide && (
          <Card className="bg-gradient-to-br from-primary/5 to-primary/10 border-primary/20 transition-opacity duration-300">
            <CardHeader 
              className="cursor-pointer group"
              onClick={() => setStepsExpanded(!stepsExpanded)}
            >
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-primary" />
                Create Your Karaoke Track in 3 Easy Steps
                <div className="ml-auto flex items-center gap-2">
                  <ChevronDown className={`h-4 w-4 transition-transform duration-300 ${stepsExpanded ? 'rotate-180' : ''}`} />
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      sessionStorage.setItem('hideStepsGuide', 'true');
                      setShowStepsGuide(false);
                    }}
                    className="p-1 rounded-full hover:bg-background/50 transition-colors"
                    title="Dismiss until next login"
                  >
                    <X className="h-4 w-4 text-muted-foreground hover:text-foreground" />
                  </button>
                </div>
              </CardTitle>
              <CardDescription className="text-base group-hover:text-foreground/70 transition-colors">
                Turn any song into a sing-along experience with synced lyrics
              </CardDescription>
            </CardHeader>
            <div 
              className={`overflow-hidden transition-all duration-500 ease-in-out ${
                stepsExpanded 
                  ? 'max-h-[1000px] opacity-100' 
                  : 'max-h-0 opacity-0'
              }`}
            >
            <CardContent className="space-y-6">
              {/* Main Karaoke Process - Step by Step */}
              <div className="space-y-4">
                {/* Step 1 */}
                <div className="flex items-start gap-4 p-4 rounded-lg bg-background/50 border border-border/50">
                  <div className="h-10 w-10 rounded-full bg-primary flex items-center justify-center flex-shrink-0 text-primary-foreground font-bold text-lg">
                    1
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-base mb-1 flex items-center gap-2">
                      <Upload className="h-4 w-4 text-primary" />
                      Upload Your Song
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      Drag and drop any audio file (MP3, WAV, M4A, and more). We support files up to 2GB.
                    </p>
                  </div>
                </div>

                {/* Step 2 */}
                <div className="flex items-start gap-4 p-4 rounded-lg bg-background/50 border border-border/50">
                  <div className="h-10 w-10 rounded-full bg-primary flex items-center justify-center flex-shrink-0 text-primary-foreground font-bold text-lg">
                    2
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-base mb-1 flex items-center gap-2">
                      <Wand2 className="h-4 w-4 text-primary" />
                      AI Creates Your Karaoke Track
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      Our AI separates vocals from the music, finds and syncs the lyrics, and creates a perfect instrumental backing track. The whole process takes just a few minutes.
                    </p>
                  </div>
                </div>

                {/* Step 3 */}
                <div className="flex items-start gap-4 p-4 rounded-lg bg-background/50 border border-border/50">
                  <div className="h-10 w-10 rounded-full bg-primary flex items-center justify-center flex-shrink-0 text-primary-foreground font-bold text-lg">
                    3
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-base mb-1 flex items-center gap-2">
                      <Mic2 className="h-4 w-4 text-primary" />
                      Sing Along!
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      Follow the synced lyrics and sing your heart out! Adjust the pitch to match your voice, and download your karaoke track to use anywhere.
                    </p>
                  </div>
                </div>
              </div>

              {/* Collapsible Tools Section */}
              <div 
                className="flex items-center gap-4 cursor-pointer group"
                onClick={() => setToolsExpanded(!toolsExpanded)}
              >
                <div className="flex-1 h-px bg-border"></div>
                <span className="text-sm text-muted-foreground font-medium flex items-center gap-2 group-hover:text-foreground transition-colors">
                  Plus More Powerful Tools
                  <ChevronDown className={`h-4 w-4 transition-transform duration-300 ${toolsExpanded ? 'rotate-180' : ''}`} />
                </span>
                <div className="flex-1 h-px bg-border"></div>
              </div>

              {/* Other Features Grid - Animated */}
              <div 
                className={`grid sm:grid-cols-3 gap-3 overflow-hidden transition-all duration-500 ease-in-out ${
                  toolsExpanded 
                    ? 'max-h-40 opacity-100' 
                    : 'max-h-0 opacity-0'
                }`}
              >
                <div className="flex items-center gap-3 p-3 rounded-lg bg-background/30 border border-border/30 hover:border-primary/30 transition-colors">
                  <Scissors className="h-5 w-5 text-orange-500 flex-shrink-0" />
                  <div>
                    <h4 className="font-medium text-sm">Vocal Removal</h4>
                    <p className="text-xs text-muted-foreground">AI-powered vocal separation</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3 p-3 rounded-lg bg-background/30 border border-border/30 hover:border-primary/30 transition-colors">
                  <BarChart3 className="h-5 w-5 text-green-500 flex-shrink-0" />
                  <div>
                    <h4 className="font-medium text-sm">Lyrics Sync</h4>
                    <p className="text-xs text-muted-foreground">Word-by-word timing accuracy</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3 p-3 rounded-lg bg-background/30 border border-border/30 hover:border-primary/30 transition-colors">
                  <Music className="h-5 w-5 text-purple-500 flex-shrink-0" />
                  <div>
                    <h4 className="font-medium text-sm">Pitch Control</h4>
                    <p className="text-xs text-muted-foreground">Adjust key to fit your voice</p>
                  </div>
                </div>
              </div>

              {/* Minutes Info */}
              <div className="flex items-center justify-between p-4 rounded-lg bg-primary/5 border border-primary/20">
                <div className="flex items-center gap-3">
                  <Coins className="h-5 w-5 text-primary" />
                  <div>
                    <p className="text-sm font-medium">Pay only for what you use</p>
                    <p className="text-xs text-muted-foreground">1 minute of audio = 1 minute charged</p>
                  </div>
                </div>
                {userMinutes > 0 && (
                  <div className="text-right">
                    <p className="text-lg font-bold text-primary">{userMinutes.toFixed(1)} min</p>
                    <p className="text-xs text-muted-foreground">available</p>
                  </div>
                )}
              </div>
            </CardContent>
            </div>
          </Card>
        )}

        {!sessionFile ? (
          /* Upload Section */
          <Card data-testid="upload-section">
            <CardHeader>
              <CardTitle>Upload Your Song</CardTitle>
              <CardDescription>
                Turn any song into a karaoke track with synced lyrics
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div
                {...getRootProps()}
                className={`group relative flex h-56 items-center justify-center rounded-lg border-2 border-dashed transition-colors cursor-pointer ${
                  isDragActive
                    ? 'border-primary bg-primary/5'
                    : 'border-border hover:border-primary/50'
                } ${uploading ? 'opacity-50 cursor-not-allowed' : ''}`}
                data-testid="upload-dropzone"
              >
                <input {...getInputProps()} />
                <div className="text-center space-y-4">
                  <div className="mx-auto h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center">
                    <Upload className="h-6 w-6 text-primary" />
                  </div>
                  {uploading ? (
                    <div className="space-y-2">
                      <p className="text-sm font-medium">Uploading...</p>
                      <Progress value={uploadProgress} className="w-64 mx-auto" data-testid="upload-progress" />
                      <p className="text-xs text-muted-foreground">{uploadProgress}%</p>
                    </div>
                  ) : (
                    <div>
                      <p className="text-sm font-medium">
                        {isDragActive ? 'Drop your audio file here' : 'Drag & drop audio or click to browse'}
                      </p>
                      <p className="text-xs text-muted-foreground mt-2">
                        Supported: MP3, WAV, FLAC, AAC, OGG, M4A, AIFF
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ) : (
          /* Session Window */
          <Card data-testid="session-window">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Session Preview</CardTitle>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleCancelSession}
                  data-testid="cancel-session-button"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <CardDescription>
                Review and edit track details before uploading
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* File Info with Artwork */}
              <div className="flex items-start space-x-4 p-4 bg-secondary/30 rounded-lg">
                {/* Artwork or Icon */}
                {sessionMetadata.artwork ? (
                  <img
                    src={sessionMetadata.artwork}
                    alt="Album artwork"
                    className="h-16 w-16 rounded-lg object-cover flex-shrink-0"
                    data-testid="session-artwork"
                  />
                ) : (
                  <div className="h-16 w-16 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <FileAudio className="h-8 w-8 text-primary" />
                  </div>
                )}
                
                {/* File Details */}
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">{sessionFile.name}</p>
                  <div className="flex items-center space-x-4 mt-2 text-sm text-muted-foreground">
                    <div className="flex items-center space-x-1">
                      <Clock className="h-3 w-3" />
                      <span>
                        {sessionFile.duration > 0 
                          ? `${Math.floor(sessionFile.duration / 60)}:${String(Math.floor(sessionFile.duration % 60)).padStart(2, '0')}`
                          : 'Loading...'}
                      </span>
                    </div>
                    <span>{(sessionFile.size / (1024 * 1024)).toFixed(2)} MB</span>
                    <span className="uppercase font-medium">{sessionFile.format || 'AUDIO'}</span>
                  </div>
                </div>
              </div>

              {/* Metadata Form */}
              <div className="grid gap-4">
                <div className="space-y-2">
                  <Label htmlFor="title">Track Title *</Label>
                  <Input
                    id="title"
                    value={sessionMetadata.title}
                    onChange={(e) => setSessionMetadata(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="Enter track title"
                    data-testid="session-title-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="artist">Artist</Label>
                  <Input
                    id="artist"
                    value={sessionMetadata.artist}
                    onChange={(e) => setSessionMetadata(prev => ({ ...prev, artist: e.target.value }))}
                    placeholder="Enter artist name (optional)"
                    data-testid="session-artist-input"
                  />
                </div>
                
                {/* Song Matching */}
                {sessionMetadata.title && sessionMetadata.artist && (
                  <div className="space-y-3">
                    <Label className="flex items-center gap-2">
                      <Music className="h-4 w-4" />
                      Lyrics Source
                    </Label>
                    
                    {musixmatchSearching ? (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground p-3 bg-secondary/30 rounded-lg">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Searching for matching song...
                      </div>
                    ) : selectedTrack && musixmatchResults.length <= 1 ? (
                      <div className="p-4 bg-secondary/30 rounded-lg border border-green-500/30">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <CheckCircle2 className="h-4 w-4 text-green-500" />
                              <span className="text-sm font-medium">Song Found</span>
                            </div>
                            <p className="mt-1 font-semibold">{selectedTrack.track_name}</p>
                            <p className="text-sm text-muted-foreground">by {selectedTrack.artist_name}</p>
                            {selectedTrack.album_name && (
                              <p className="text-xs text-muted-foreground mt-1">Album: {selectedTrack.album_name}</p>
                            )}
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={handleSearchAgain}
                          >
                            Search Again
                          </Button>
                        </div>
                      </div>
                    ) : showManualSearchInput ? (
                      <div className="p-4 bg-secondary/30 rounded-lg space-y-3">
                        <div className="text-sm font-medium">Search for the correct song:</div>
                        <div className="grid gap-2">
                          <Input
                            placeholder="Artist name"
                            value={manualSearchArtist}
                            onChange={(e) => setManualSearchArtist(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleManualSearch()}
                          />
                          <Input
                            placeholder="Song title"
                            value={manualSearchTitle}
                            onChange={(e) => setManualSearchTitle(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleManualSearch()}
                          />
                          <Button
                            onClick={handleManualSearch}
                            disabled={musixmatchSearching}
                            size="sm"
                          >
                            {musixmatchSearching ? (
                              <>
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                Searching...
                              </>
                            ) : (
                              'Search'
                            )}
                          </Button>
                        </div>
                        
                        {musixmatchResults.length > 0 && (
                          <div className="space-y-2 mt-3 pt-3 border-t">
                            <div className="text-sm text-muted-foreground">Select the correct song:</div>
                            <div className="space-y-2 max-h-48 overflow-y-auto">
                              {musixmatchResults.map((track) => (
                                <div
                                  key={track.commontrack_id}
                                  className={`p-3 rounded-lg cursor-pointer transition-colors ${
                                    selectedTrack?.commontrack_id === track.commontrack_id
                                      ? 'bg-primary/20 border border-primary'
                                      : 'bg-background hover:bg-secondary/50 border border-transparent'
                                  }`}
                                  onClick={() => handleConfirmTrack(track)}
                                >
                                  <p className="font-medium">{track.track_name}</p>
                                  <p className="text-sm text-muted-foreground">by {track.artist_name}</p>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {lyricsError && (
                          <div className="text-sm text-muted-foreground">{lyricsError}</div>
                        )}
                      </div>
                    ) : musixmatchResults.length > 1 ? (
                      <div className="p-4 bg-secondary/30 rounded-lg space-y-3">
                        <div className="flex items-center justify-between">
                          <div className="text-sm font-medium">Multiple matches found. Select the correct song:</div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={handleSearchAgain}
                          >
                            Search Again
                          </Button>
                        </div>
                        <div className="space-y-2 max-h-48 overflow-y-auto">
                          {musixmatchResults.map((track) => (
                            <div
                              key={track.commontrack_id}
                              className={`p-3 rounded-lg cursor-pointer transition-colors ${
                                selectedTrack?.commontrack_id === track.commontrack_id
                                  ? 'bg-primary/20 border border-primary'
                                  : 'bg-background hover:bg-secondary/50 border border-transparent'
                              }`}
                              onClick={() => handleConfirmTrack(track)}
                            >
                              <p className="font-medium">{track.track_name}</p>
                              <p className="text-sm text-muted-foreground">by {track.artist_name}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : lyricsError ? (
                      <div className="p-3 bg-secondary/30 rounded-lg text-sm text-muted-foreground flex items-center justify-between">
                        <span>{lyricsError}</span>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleSearchAgain}
                        >
                          Try Again
                        </Button>
                      </div>
                    ) : null}
                  </div>
                )}
              </div>

              {/* Upload Button */}
              <div className="flex justify-end pt-4 border-t">
                <Button
                  onClick={handleUploadAndAnalyze}
                  disabled={!sessionMetadata.title || uploading}
                  size="lg"
                  data-testid="upload-analyze-button"
                >
                  {uploading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-4 w-4 mr-2" />
                      Create Karaoke Track
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Shared Song Library - Ready to Sing */}
        {!sessionFile && (
          <Card data-testid="shared-songs-section">
            <CardHeader>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <CardTitle>Ready to Sing</CardTitle>
                  <CardDescription>
                    Browse all available karaoke tracks ({sharedSongsTotal} songs)
                  </CardDescription>
                </div>
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                  <div className="relative w-full sm:w-64">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search title, artist, uploader..."
                      value={sharedSongsSearch}
                      onChange={handleSharedSongsSearch}
                      className="pl-9"
                    />
                  </div>
                  <label className="flex items-center gap-2 cursor-pointer whitespace-nowrap text-sm">
                    <input
                      type="checkbox"
                      checked={myUploadsOnly}
                      onChange={handleMyUploadsToggle}
                      className="w-4 h-4 rounded border-gray-600 bg-secondary text-primary focus:ring-primary focus:ring-offset-0"
                    />
                    <span className="text-muted-foreground">My uploads only</span>
                  </label>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {sharedSongsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-primary" />
                </div>
              ) : sharedSongs.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  {sharedSongsSearch ? 'No songs match your search' : 'No songs available yet. Upload your first track!'}
                </div>
              ) : (
                <>
                  {/* Mobile card view */}
                  <div className="md:hidden space-y-3">
                    {sharedSongs.map((session) => (
                      <div
                        key={session.id}
                        data-testid={`shared-song-card-${session.id}`}
                        draggable
                        onDragStart={(e) => handleSongDragStart(e, session)}
                        className="p-4 bg-secondary/30 rounded-lg space-y-3 cursor-grab active:cursor-grabbing"
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <h3 className="font-medium truncate">{cleanTitle(session.title || session.filename)}</h3>
                            <p className="text-sm text-muted-foreground truncate">{session.artist || 'Unknown Artist'}</p>
                          </div>
                          <Badge variant={session.enhanced?.words?.length > 0 || session.transcription?.words?.length > 0 ? 'default' : 'secondary'} className="shrink-0">
                            {session.enhanced?.words?.length > 0 || session.transcription?.words?.length > 0 ? 'Synced' : 'No Lyrics'}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-4 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {formatTime(session.duration)}
                          </span>
                          <span className="flex items-center gap-1">
                            <UserCircle className="h-3 w-3" />
                            {session.uploaded_by_name || 'Unknown'}
                          </span>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            onClick={() => navigate(`/sing/${session.id}`)}
                            className="flex-1 bg-gradient-to-r from-primary to-purple-500 hover:from-primary/90 hover:to-purple-500/90"
                          >
                            <Mic2 className="h-4 w-4 mr-2" />
                            Sing Now
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setInfoSession(session)}
                            title="Info"
                          >
                            <Info className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleEditSession(session)}
                            title="Edit"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setDeleteConfirmSession(session)}
                            title="Delete"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {/* Desktop table view */}
                  <div className="hidden md:block">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Title</TableHead>
                          <TableHead>Artist</TableHead>
                          <TableHead>Duration</TableHead>
                          <TableHead>Lyrics</TableHead>
                          <TableHead>Uploaded By</TableHead>
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {sharedSongs.map((session) => (
                          <TableRow 
                            key={session.id}
                            draggable
                            onDragStart={(e) => handleSongDragStart(e, session)}
                            className="cursor-grab active:cursor-grabbing"
                          >
                            <TableCell className="font-medium">
                              <div className="flex items-center gap-2">
                                <GripVertical className="h-4 w-4 text-muted-foreground/50" />
                                {cleanTitle(session.title || session.filename)}
                              </div>
                            </TableCell>
                            <TableCell className="text-muted-foreground">
                              {session.artist || '-'}
                            </TableCell>
                            <TableCell className="text-muted-foreground">
                              {formatTime(session.duration)}
                            </TableCell>
                            <TableCell>
                              <Badge variant={session.enhanced?.words?.length > 0 || session.transcription?.words?.length > 0 ? 'default' : 'secondary'}>
                                {session.enhanced?.words?.length > 0 || session.transcription?.words?.length > 0 ? 'Synced' : 'No Lyrics'}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-muted-foreground">
                              {session.uploaded_by_name || 'Unknown'}
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex justify-end gap-2">
                                <Button
                                  size="sm"
                                  onClick={() => navigate(`/sing/${session.id}`)}
                                  className="bg-gradient-to-r from-primary to-purple-500 hover:from-primary/90 hover:to-purple-500/90"
                                >
                                  <Mic2 className="h-4 w-4 mr-2" />
                                  Sing Now
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => setInfoSession(session)}
                                  title="Info"
                                >
                                  <Info className="h-4 w-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleEditSession(session)}
                                  title="Edit"
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => setDeleteConfirmSession(session)}
                                  title="Delete"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                  
                  {/* Pagination */}
                  {sharedSongsTotalPages > 1 && (
                    <div className="flex items-center justify-center gap-2 mt-4">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleSharedSongsPageChange(sharedSongsPage - 1)}
                        disabled={sharedSongsPage === 1}
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      <span className="text-sm text-muted-foreground">
                        Page {sharedSongsPage} of {sharedSongsTotalPages}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleSharedSongsPageChange(sharedSongsPage + 1)}
                        disabled={sharedSongsPage === sharedSongsTotalPages}
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        )}

        {/* Party Playlist */}
        {!sessionFile && (
          <Card data-testid="party-playlist-section">
            <CardHeader>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-gradient-to-r from-pink-500 to-orange-500 flex items-center justify-center">
                    <PartyPopper className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      Party Mode
                      {editingPartyId && <Badge variant="outline">Editing</Badge>}
                      {partyPlaylist.length > 0 && (
                        <Badge variant="secondary">{partyPlaylist.length} songs</Badge>
                      )}
                    </CardTitle>
                    <CardDescription>
                      Drag songs from above to create a playlist
                    </CardDescription>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Input
                    placeholder="Name your party..."
                    value={partyName}
                    onChange={(e) => setPartyName(e.target.value)}
                    className="w-40 sm:w-48"
                  />
                  {partyPlaylist.length > 0 && (
                    <>
                      <Button
                        onClick={saveParty}
                        disabled={savingParty}
                        variant="outline"
                        size="sm"
                      >
                        {savingParty ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
                        {editingPartyId ? 'Update' : 'Save'}
                      </Button>
                      <Button
                        onClick={startParty}
                        className="bg-gradient-to-r from-pink-500 to-orange-500 hover:from-pink-600 hover:to-orange-600"
                      >
                        <Play className="h-4 w-4 mr-2" />
                        Start
                      </Button>
                    </>
                  )}
                  {editingPartyId && (
                    <Button
                      onClick={clearPartyEditor}
                      variant="ghost"
                      size="sm"
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      New
                    </Button>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div
                onDrop={handlePartyDrop}
                onDragOver={handlePartyDragOver}
                onDragLeave={handlePartyDragLeave}
                className={`min-h-[120px] rounded-lg border-2 border-dashed transition-all ${
                  dragOverParty 
                    ? 'border-pink-500 bg-pink-500/10' 
                    : 'border-muted-foreground/20 bg-secondary/20'
                }`}
              >
                {partyPlaylist.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-[120px] text-muted-foreground">
                    <Plus className="h-8 w-8 mb-2 opacity-50" />
                    <p className="text-sm">Drag songs here to add them to your party</p>
                  </div>
                ) : (
                  <div className="p-3 space-y-2">
                    {partyPlaylist.map((song, index) => (
                      <div
                        key={`${song.id}-${index}`}
                        draggable
                        onDragStart={(e) => handlePartyItemDragStart(e, index)}
                        onDragOver={(e) => handlePartyItemDragOver(e, index)}
                        onDrop={(e) => handlePartyItemDrop(e, index)}
                        onDragEnd={handlePartyItemDragEnd}
                        className={`flex items-center gap-3 p-3 rounded-lg transition-all ${
                          dragOverIndex === index 
                            ? 'bg-pink-500/20 border border-pink-500' 
                            : 'bg-secondary/40 hover:bg-secondary/60'
                        } ${draggedPartyIndex === index ? 'opacity-50' : ''}`}
                      >
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <GripVertical className="h-4 w-4 cursor-grab active:cursor-grabbing" />
                          <span className="text-sm font-medium w-6">{index + 1}.</span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">{song.title}</p>
                          <p className="text-sm text-muted-foreground truncate">{song.artist}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="hidden sm:flex items-center gap-2">
                            <Mic className="h-4 w-4 text-muted-foreground" />
                            <Input
                              placeholder="Singer name..."
                              value={song.singer}
                              onChange={(e) => updatePartySinger(index, e.target.value)}
                              className="w-32 h-8 text-sm"
                              onClick={(e) => e.stopPropagation()}
                            />
                          </div>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => removeFromParty(index)}
                            className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {partyPlaylist.length > 0 && (
                <div className="mt-3 flex items-center justify-between text-sm text-muted-foreground">
                  <span>Total: {partyPlaylist.length} songs</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setPartyPlaylist([])}
                    className="text-muted-foreground hover:text-destructive"
                  >
                    Clear All
                  </Button>
                </div>
              )}
              
              {/* Saved Parties List */}
              {savedParties.length > 0 && (
                <div className="mt-6 pt-4 border-t">
                  <h4 className="text-sm font-medium mb-3 flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    Saved Parties
                  </h4>
                  <div className="space-y-2">
                    {savedParties.map((party) => (
                      <div
                        key={party.id}
                        className={`flex items-center justify-between p-3 rounded-lg transition-all ${
                          editingPartyId === party.id 
                            ? 'bg-pink-500/20 border border-pink-500' 
                            : 'bg-secondary/30 hover:bg-secondary/50'
                        }`}
                      >
                        <div className="min-w-0 flex-1">
                          <p className="font-medium truncate">{party.name}</p>
                          <div className="flex items-center gap-3 text-xs text-muted-foreground">
                            <span>{party.songs?.length || 0} songs</span>
                            <span>{new Date(party.created_at).toLocaleDateString()}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => loadParty(party)}
                            className="h-8 px-2"
                            title="Edit party"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => {
                              const partyData = { name: party.name, songs: party.songs };
                              sessionStorage.setItem('partyPlaylist', JSON.stringify(partyData));
                              navigate(`/sing/${party.songs[0].id}?party=true`);
                            }}
                            disabled={!party.songs?.length}
                            className="h-8 bg-gradient-to-r from-pink-500 to-orange-500 hover:from-pink-600 hover:to-orange-600"
                          >
                            <Play className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => deleteParty(party.id)}
                            className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive"
                            title="Delete party"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Sessions History */}
        {!sessionFile && allHistorySessions.length > 0 && (
          <Card data-testid="sessions-history-section">
            <CardHeader>
              <CardTitle>My Karaoke Library</CardTitle>
              <CardDescription>
                Your saved karaoke tracks
              </CardDescription>
            </CardHeader>
            <CardContent>
              {/* Mobile card view */}
              <div className="md:hidden space-y-3">
                {historySessions.map((session) => (
                  <div
                    key={session.id}
                    data-testid={`history-session-card-${session.id}`}
                    className="p-4 bg-secondary/30 rounded-lg space-y-3"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <h3 className="font-medium truncate">{cleanTitle(session.title || session.filename)}</h3>
                        <p className="text-sm text-muted-foreground truncate">{session.artist || 'Unknown Artist'}</p>
                      </div>
                      <Badge variant={session.transcription?.words?.length > 0 ? 'default' : 'secondary'} className="shrink-0">
                        {session.transcription?.words?.length > 0 ? 'Synced' : 'Pending'}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {formatTime(session.duration)}
                      </span>
                      <span>{new Date(session.created_at).toLocaleDateString()}</span>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={() => navigate(`/sing/${session.id}`)}
                        data-testid={`sing-history-session-${session.id}`}
                        className="flex-1 bg-gradient-to-r from-primary to-purple-500 hover:from-primary/90 hover:to-purple-500/90"
                      >
                        <Mic2 className="h-4 w-4 mr-2" />
                        Sing
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleEditSession(session)}
                        data-testid={`view-session-${session.id}`}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => archiveSession(session.id)}
                        data-testid={`archive-session-${session.id}`}
                      >
                        <Archive className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Desktop table view */}
              <div className="hidden md:block">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Title</TableHead>
                      <TableHead>Artist</TableHead>
                      <TableHead>Duration</TableHead>
                      <TableHead>Lyrics</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {historySessions.map((session) => (
                      <TableRow 
                        key={session.id}
                        data-testid={`history-session-row-${session.id}`}
                      >
                        <TableCell className="font-medium">
                          {cleanTitle(session.title || session.filename)}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {session.artist || '-'}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {formatTime(session.duration)}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          <Badge variant={session.transcription?.words?.length > 0 ? 'default' : 'secondary'}>
                            {session.transcription?.words?.length > 0 ? 'Synced' : 'Pending'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {new Date(session.created_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              size="sm"
                              onClick={() => navigate(`/sing/${session.id}`)}
                              data-testid={`sing-history-session-${session.id}`}
                              className="bg-gradient-to-r from-primary to-purple-500 hover:from-primary/90 hover:to-purple-500/90"
                            >
                              <Mic2 className="h-4 w-4 mr-2" />
                              Sing
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleEditSession(session)}
                              data-testid={`view-session-${session.id}`}
                            >
                              <Edit className="h-4 w-4 mr-2" />
                              Edit
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => archiveSession(session.id)}
                              data-testid={`archive-session-${session.id}`}
                            >
                              <Archive className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              
              {/* Pagination Controls */}
              {totalHistoryPages > 1 && (
                <div className="flex flex-col sm:flex-row items-center justify-between gap-3 mt-4 pt-4 border-t">
                  <div className="text-sm text-muted-foreground text-center sm:text-left">
                    {((historyPage - 1) * HISTORY_PER_PAGE) + 1} - {Math.min(historyPage * HISTORY_PER_PAGE, allHistorySessions.length)} of {allHistorySessions.length}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleHistoryPageChange(historyPage - 1)}
                      disabled={historyPage === 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                      <span className="hidden sm:inline">Previous</span>
                    </Button>
                    <div className="hidden sm:flex items-center gap-1">
                      {Array.from({ length: totalHistoryPages }, (_, i) => i + 1)
                        .filter(page => {
                          if (totalHistoryPages <= 5) return true;
                          if (page === 1 || page === totalHistoryPages) return true;
                          if (Math.abs(page - historyPage) <= 1) return true;
                          return false;
                        })
                        .map((page, index, arr) => (
                          <React.Fragment key={page}>
                            {index > 0 && arr[index - 1] !== page - 1 && (
                              <span className="px-1 text-muted-foreground">...</span>
                            )}
                            <Button
                              variant={historyPage === page ? "default" : "outline"}
                              size="sm"
                              className="w-8 h-8 p-0"
                              onClick={() => handleHistoryPageChange(page)}
                            >
                              {page}
                            </Button>
                          </React.Fragment>
                        ))
                      }
                    </div>
                    <span className="sm:hidden text-sm text-muted-foreground">
                      {historyPage} / {totalHistoryPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleHistoryPageChange(historyPage + 1)}
                      disabled={historyPage === totalHistoryPages}
                    >
                      <span className="hidden sm:inline">Next</span>
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Archived Sessions */}
        {!sessionFile && archivedSessions.length > 0 && (
          <Card data-testid="archived-sessions-section">
            <CardHeader
              onClick={() => setArchivedExpanded(!archivedExpanded)}
              className="cursor-pointer hover:bg-secondary/20 transition-colors"
            >
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Archive className="h-5 w-5" />
                    Archived Songs
                    <Badge variant="outline">{archivedSessions.length}</Badge>
                  </CardTitle>
                  <CardDescription>
                    Songs that have been archived
                  </CardDescription>
                </div>
                {archivedExpanded ? <ChevronDown className="h-5 w-5" /> : <ChevronDown className="h-5 w-5 transform -rotate-90" />}
              </div>
            </CardHeader>
            {archivedExpanded && (
              <CardContent>
                {/* Mobile card view */}
                <div className="md:hidden space-y-3">
                  {archivedSessions.map((session) => (
                    <div
                      key={session.id}
                      data-testid={`archived-session-card-${session.id}`}
                      className="p-4 bg-secondary/20 rounded-lg space-y-3 opacity-75"
                    >
                      <div className="min-w-0">
                        <h3 className="font-medium truncate text-muted-foreground">{cleanTitle(session.title || session.filename)}</h3>
                        <p className="text-sm text-muted-foreground truncate">{session.artist || 'Unknown Artist'}</p>
                      </div>
                      <div className="flex items-center gap-4 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {formatTime(session.duration)}
                        </span>
                        <span>{new Date(session.created_at).toLocaleDateString()}</span>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => restoreSession(session.id)}
                          data-testid={`restore-session-${session.id}`}
                          className="flex-1"
                        >
                          <ArchiveRestore className="h-4 w-4 mr-2" />
                          Restore
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setDeleteConfirmSession(session)}
                          data-testid={`delete-session-${session.id}`}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Desktop table view */}
                <div className="hidden md:block">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Title</TableHead>
                        <TableHead>Artist</TableHead>
                        <TableHead>Duration</TableHead>
                        <TableHead>Created</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {archivedSessions.map((session) => (
                        <TableRow 
                          key={session.id}
                          data-testid={`archived-session-row-${session.id}`}
                        >
                          <TableCell className="font-medium text-muted-foreground">
                            {cleanTitle(session.title || session.filename)}
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            {session.artist || '-'}
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            {formatTime(session.duration)}
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            {new Date(session.created_at).toLocaleDateString()}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => restoreSession(session.id)}
                                data-testid={`restore-session-${session.id}`}
                              >
                                <ArchiveRestore className="h-4 w-4 mr-2" />
                                Restore
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => setDeleteConfirmSession(session)}
                                data-testid={`delete-session-${session.id}`}
                              >
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            )}
          </Card>
        )}
      </div>

      {/* Processing Modal */}
      <ProcessingModal 
        isOpen={!!processingProject}
        status={processingProject?.status || 'uploading'}
        sessionId={processingProject?.id}
        onClose={() => {}}
        onCancel={() => {
          setProcessingProject(null);
          fetchSessions();
        }}
        onBackground={() => {
          setProcessingProject(null);
        }}
      />

      {/* Upload Credit Confirmation Modal */}
      <CreditConfirmModal
        isOpen={showUploadCreditModal}
        onClose={() => setShowUploadCreditModal(false)}
        onConfirm={handleUploadConfirmed}
        operationName="Create Karaoke Track"
        operationCost={Math.ceil((sessionFile?.duration || 0) / 60)}
        currentCredits={userMinutes}
        icon={Sparkles}
      />

      {/* No Credits Modal */}
      <Dialog.Root open={showNoCreditsModal} onOpenChange={setShowNoCreditsModal}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/50 z-50" />
          <Dialog.Content className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-card border border-border rounded-xl shadow-xl z-50 w-full max-w-md p-6">
            <div className="text-center mb-6">
              <div className="mx-auto h-16 w-16 rounded-full bg-orange-500/10 flex items-center justify-center mb-4">
                <CreditCard className="h-8 w-8 text-orange-500" />
              </div>
              <Dialog.Title className="text-xl font-bold mb-2">
                Not Enough Minutes
              </Dialog.Title>
              <Dialog.Description className="text-muted-foreground">
                {sessionFile && (
                  <>
                    This song is <span className="font-semibold text-foreground">{Math.ceil((sessionFile.duration || 0) / 60)} minutes</span> long and requires {Math.ceil((sessionFile.duration || 0) / 60)} minutes.
                    <br />
                    You currently have <span className="font-semibold text-foreground">{userMinutes.toFixed(1)} minutes</span> available.
                  </>
                )}
              </Dialog.Description>
            </div>

            <div className="bg-secondary/30 rounded-lg p-4 mb-6">
              <h4 className="font-semibold mb-2 flex items-center gap-2">
                <Info className="h-4 w-4 text-primary" />
                How Pricing Works
              </h4>
              <p className="text-sm text-muted-foreground">
                Minutes are deducted based on song length. A 3-minute song uses 3 minutes, a 5-minute song uses 5 minutes, and so on. This ensures you only pay for what you use.
              </p>
            </div>

            {packages.length > 0 && (
              <div className="mb-6">
                <h4 className="font-semibold mb-3">Available Packages</h4>
                <div className="grid grid-cols-3 gap-2">
                  {packages.slice(0, 3).map((pkg) => (
                    <button
                      key={pkg.minutes}
                      onClick={() => !isProcessingPayment && setSelectedPackage(pkg.minutes)}
                      disabled={isProcessingPayment}
                      className={`text-center p-3 rounded-lg border transition-all cursor-pointer ${
                        selectedPackage === pkg.minutes
                          ? 'bg-primary/20 border-primary ring-2 ring-primary/50'
                          : 'bg-secondary/30 border-border hover:border-primary/50 hover:bg-secondary/50'
                      } ${isProcessingPayment ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <div className="font-bold text-lg">{pkg.minutes} min</div>
                      <div className="text-sm text-muted-foreground">${pkg.price.toFixed(2)}</div>
                      {selectedPackage === pkg.minutes && (
                        <CheckCircle2 className="h-4 w-4 text-primary mx-auto mt-1" />
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => {
                  setShowNoCreditsModal(false);
                  setSelectedPackage(null);
                }}
                disabled={isProcessingPayment}
              >
                Cancel
              </Button>
              <Button
                className="flex-1"
                onClick={() => {
                  if (selectedPackage) {
                    purchaseMinutes(selectedPackage);
                  } else {
                    toast.error('Please select a package first');
                  }
                }}
                disabled={!selectedPackage || isProcessingPayment}
              >
                {isProcessingPayment ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <CreditCard className="h-4 w-4 mr-2" />
                    Buy Minutes
                  </>
                )}
              </Button>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      {/* Welcome Modal */}
      <Dialog.Root open={showWelcomeModal} onOpenChange={setShowWelcomeModal}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/50 z-50" />
          <Dialog.Content className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-card border border-primary/20 rounded-xl shadow-xl z-50 w-full max-w-lg p-8">
            <div className="text-center">
              <div className="mx-auto h-20 w-20 rounded-full bg-gradient-to-r from-primary/20 to-purple-500/20 flex items-center justify-center mb-6">
                <Music className="h-10 w-10 text-primary" />
              </div>
              <Dialog.Title className="text-3xl font-bold mb-3 bg-gradient-to-r from-primary to-purple-400 bg-clip-text text-transparent">
                Welcome to Allioki!
              </Dialog.Title>
              <Dialog.Description className="text-muted-foreground text-lg mb-6">
                You've been credited with <span className="font-bold text-primary text-xl">5 free minutes</span> to start your karaoke journey.
              </Dialog.Description>

              <div className="bg-secondary/30 rounded-lg p-6 mb-6 text-left">
                <h4 className="font-semibold mb-4 flex items-center gap-2 text-foreground">
                  <Info className="h-5 w-5 text-primary" />
                  What You Can Do:
                </h4>
                <div className="space-y-3 text-sm text-muted-foreground">
                  <div className="flex items-start gap-3">
                    <Wand2 className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                    <div>
                      <span className="font-semibold text-foreground">Create Karaoke Tracks</span>
                      <p className="text-xs mt-0.5">Turn any song into a sing-along experience</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <FileText className="h-5 w-5 text-purple-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <span className="font-semibold text-foreground">Synced Lyrics</span>
                      <p className="text-xs mt-0.5">Follow along with perfectly timed lyrics</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Scissors className="h-5 w-5 text-orange-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <span className="font-semibold text-foreground">Vocal Removal</span>
                      <p className="text-xs mt-0.5">Crystal-clear instrumental backing tracks</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Mic2 className="h-5 w-5 text-blue-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <span className="font-semibold text-foreground">Pitch Adjustment</span>
                      <p className="text-xs mt-0.5">Match the song to your vocal range</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-r from-primary/10 to-purple-500/10 border border-primary/20 rounded-lg p-4 mb-6">
                <p className="text-sm text-foreground">
                  <span className="font-semibold">How it works:</span> Credits are used based on song duration. A 3-minute song uses 3 credits.
                </p>
              </div>

              <Button
                size="lg"
                onClick={() => {
                  if (userProfile?.id) {
                    localStorage.setItem(`hasSeenWelcome:${userProfile.id}`, 'true');
                  }
                  setShowWelcomeModal(false);
                }}
                className="w-full bg-gradient-to-r from-primary to-purple-500 hover:from-primary/90 hover:to-purple-500/90"
              >
                Start Singing
                <Sparkles className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      {/* Delete Confirmation Modal */}
      <Dialog.Root open={!!deleteConfirmSession} onOpenChange={(open) => !open && setDeleteConfirmSession(null)}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/50 z-50" />
          <Dialog.Content className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-card border border-border rounded-xl shadow-xl z-50 w-full max-w-md p-6">
            <div className="text-center mb-6">
              <div className="mx-auto h-16 w-16 rounded-full bg-destructive/10 flex items-center justify-center mb-4">
                <Trash2 className="h-8 w-8 text-destructive" />
              </div>
              <Dialog.Title className="text-xl font-bold mb-2">
                Delete Session Permanently?
              </Dialog.Title>
              <Dialog.Description className="text-muted-foreground">
                Are you sure you want to permanently delete <span className="font-semibold text-foreground">{deleteConfirmSession?.title || deleteConfirmSession?.filename}</span>?
                <br /><br />
                This action cannot be undone. All associated files will be permanently removed.
              </Dialog.Description>
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setDeleteConfirmSession(null)}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                className="flex-1"
                onClick={() => deleteSession(deleteConfirmSession.id)}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete Permanently
              </Button>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      {/* Lyrics Edit Modal */}
      <Dialog.Root open={!!editLyricsSession} onOpenChange={(open) => !open && setEditLyricsSession(null)}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/50 z-50" />
          <Dialog.Content className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-card border border-border rounded-xl shadow-xl z-50 w-full max-w-4xl max-h-[90vh] flex flex-col">
            <div className="p-6 border-b border-border">
              <div className="flex items-center justify-between">
                <Dialog.Title className="text-xl font-bold">
                  Edit Song
                </Dialog.Title>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => {
                    setEditLyricsSession(null);
                    setLyricsSearchResults([]);
                    setSelectedLyricsTrack(null);
                    setPreviewLyrics('');
                  }}
                >
                  <X className="h-5 w-5" />
                </Button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4">
                <div>
                  <Label className="text-sm text-muted-foreground mb-1 block">Title</Label>
                  <Input
                    value={editedTitle}
                    onChange={(e) => setEditedTitle(e.target.value)}
                    placeholder="Song title"
                  />
                </div>
                <div>
                  <Label className="text-sm text-muted-foreground mb-1 block">Artist</Label>
                  <Input
                    value={editedArtist}
                    onChange={(e) => setEditedArtist(e.target.value)}
                    placeholder="Artist name"
                  />
                </div>
                <div className="flex items-end">
                  <Button
                    onClick={handleSearchLyrics}
                    disabled={lyricsSearchLoading || (!editedTitle && !editedArtist)}
                    className="w-full"
                    variant="outline"
                  >
                    {lyricsSearchLoading ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Searching...
                      </>
                    ) : (
                      <>
                        <Search className="h-4 w-4 mr-2" />
                        Search Lyrics
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
            
            <div className="flex-1 p-6 overflow-hidden flex gap-4">
              {/* Left side - Lyrics Editor */}
              <div className="flex-1 flex flex-col min-w-0">
                <div className="flex items-center justify-between mb-3">
                  <Label className="text-sm text-muted-foreground">
                    Edit lyrics below. Use "Save & Enhance" to sync with audio.
                  </Label>
                  {originalLyrics && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleResetLyrics}
                      className="text-muted-foreground hover:text-foreground shrink-0"
                    >
                      <RotateCcw className="h-4 w-4 mr-1" />
                      Reset
                    </Button>
                  )}
                </div>
                <textarea
                  value={editedLyrics}
                  onChange={(e) => setEditedLyrics(e.target.value)}
                  className="flex-1 w-full bg-background border border-border rounded-lg p-4 text-sm font-mono resize-none focus:outline-none focus:ring-2 focus:ring-primary/50"
                  placeholder="Paste or type lyrics here..."
                  style={{ minHeight: '300px' }}
                />
              </div>
              
              {/* Right side - Search Results & Preview */}
              {(lyricsSearchResults.length > 0 || previewLyrics) && (
                <div className="w-80 flex flex-col border-l border-border pl-4">
                  {lyricsSearchResults.length > 0 && !previewLyrics && (
                    <div className="flex-1 flex flex-col">
                      <Label className="text-sm font-medium mb-2">Found {lyricsSearchResults.length} result(s)</Label>
                      <div className="flex-1 overflow-y-auto space-y-2">
                        {lyricsSearchResults.map((track, idx) => (
                          <button
                            key={idx}
                            onClick={() => handleSelectLyricsTrack(track)}
                            className={`w-full text-left p-3 rounded-lg border transition-colors ${
                              selectedLyricsTrack?.commontrack_id === track.commontrack_id
                                ? 'border-primary bg-primary/10'
                                : 'border-border hover:border-primary/50 hover:bg-secondary/30'
                            }`}
                          >
                            <div className="font-medium text-sm truncate">{track.track_name}</div>
                            <div className="text-xs text-muted-foreground truncate">{track.artist_name}</div>
                            {track.album_name && (
                              <div className="text-xs text-muted-foreground/70 truncate mt-1">{track.album_name}</div>
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {loadingPreviewLyrics && (
                    <div className="flex-1 flex items-center justify-center">
                      <Loader2 className="h-6 w-6 animate-spin text-primary" />
                    </div>
                  )}
                  
                  {previewLyrics && !loadingPreviewLyrics && (
                    <div className="flex-1 flex flex-col">
                      <div className="flex items-center justify-between mb-2">
                        <Label className="text-sm font-medium">Preview Lyrics</Label>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setPreviewLyrics('');
                            setSelectedLyricsTrack(null);
                          }}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                      <div className="flex-1 overflow-y-auto bg-secondary/30 rounded-lg p-3 text-xs font-mono whitespace-pre-wrap" style={{ maxHeight: '280px' }}>
                        {previewLyrics}
                      </div>
                      <Button
                        onClick={handleApplyLyrics}
                        className="mt-3 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600"
                      >
                        <CheckCircle2 className="h-4 w-4 mr-2" />
                        Apply These Lyrics
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="p-6 border-t border-border flex gap-3 justify-between">
              <Button
                variant="outline"
                onClick={() => {
                  setEditLyricsSession(null);
                  setLyricsSearchResults([]);
                  setSelectedLyricsTrack(null);
                  setPreviewLyrics('');
                }}
              >
                Cancel
              </Button>
              <div className="flex gap-3">
                <Button
                  onClick={handleEnhanceLyrics}
                  disabled={enhancingLyrics || !editedLyrics.trim()}
                  className="bg-gradient-to-r from-primary to-purple-500 hover:from-primary/90 hover:to-purple-500/90"
                >
                  {enhancingLyrics ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Enhancing...
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-4 w-4 mr-2" />
                      Save & Enhance
                    </>
                  )}
                </Button>
              </div>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      {/* Song Info Modal */}
      <Dialog.Root open={!!infoSession} onOpenChange={(open) => !open && setInfoSession(null)}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50" />
          <Dialog.Content className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-card border border-border rounded-xl shadow-2xl w-[95vw] max-w-md z-50 max-h-[90vh] overflow-auto">
            <div className="p-6 border-b border-border">
              <Dialog.Title className="text-xl font-semibold">Song Information</Dialog.Title>
            </div>
            
            {infoSession && (
              <div className="p-6 space-y-4">
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground uppercase tracking-wide">Title</Label>
                  <p className="font-medium">{cleanTitle(infoSession.title || infoSession.filename)}</p>
                </div>
                
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground uppercase tracking-wide">Artist</Label>
                  <p className="font-medium">{infoSession.artist || 'Unknown'}</p>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground uppercase tracking-wide">Duration</Label>
                    <p className="font-medium flex items-center gap-1">
                      <Clock className="h-4 w-4 text-muted-foreground" />
                      {formatTime(infoSession.duration)}
                    </p>
                  </div>
                  
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground uppercase tracking-wide">Lyrics Status</Label>
                    <div className="pt-0.5">
                      <Badge variant={infoSession.enhanced?.words?.length > 0 || infoSession.transcription?.words?.length > 0 ? 'default' : 'secondary'}>
                        {infoSession.enhanced?.words?.length > 0 || infoSession.transcription?.words?.length > 0 ? 'Synced' : 'No Lyrics'}
                      </Badge>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground uppercase tracking-wide">Uploaded By</Label>
                  <p className="font-medium flex items-center gap-2">
                    <UserCircle className="h-4 w-4 text-muted-foreground" />
                    {infoSession.uploaded_by_name || 'Unknown'}
                  </p>
                </div>
                
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground uppercase tracking-wide">Upload Date</Label>
                  <p className="font-medium flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    {new Date(infoSession.created_at).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                </div>
                
                {infoSession.source_lyrics && (
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground uppercase tracking-wide">Lyrics Source</Label>
                    <Badge variant="outline">Musixmatch</Badge>
                  </div>
                )}
                
                {infoSession.files?.instrumental_url && (
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground uppercase tracking-wide">Audio Files</Label>
                    <div className="flex flex-wrap gap-2">
                      <div className="flex items-center gap-1">
                        <Badge variant="outline" className="bg-green-500/10 text-green-400 border-green-500/30">Instrumental</Badge>
                        <a
                          href={infoSession.files.instrumental_url}
                          download={`${infoSession.title || 'Track'} (Instrumental).mp3`}
                          className="p-1 rounded hover:bg-green-500/20 transition-colors"
                          title="Download Instrumental"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Download className="h-3.5 w-3.5 text-green-400" />
                        </a>
                      </div>
                      {infoSession.files?.vocal_url && (
                        <div className="flex items-center gap-1">
                          <Badge variant="outline" className="bg-blue-500/10 text-blue-400 border-blue-500/30">Vocals</Badge>
                          <a
                            href={infoSession.files.vocal_url}
                            download={`${infoSession.title || 'Track'} (Vocal).mp3`}
                            className="p-1 rounded hover:bg-blue-500/20 transition-colors"
                            title="Download Vocals"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Download className="h-3.5 w-3.5 text-blue-400" />
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
            
            <div className="p-6 border-t border-border flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => setInfoSession(null)}
              >
                Close
              </Button>
              <Button
                onClick={() => {
                  navigate(`/sing/${infoSession.id}`);
                  setInfoSession(null);
                }}
                className="bg-gradient-to-r from-primary to-purple-500 hover:from-primary/90 hover:to-purple-500/90"
              >
                <Mic2 className="h-4 w-4 mr-2" />
                Sing This Song
              </Button>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      {/* Screen Name Setup Modal */}
      <Dialog.Root open={showScreenNameModal} onOpenChange={() => {}}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50" />
          <Dialog.Content className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-card border border-border rounded-xl shadow-2xl w-[95vw] max-w-md z-50">
            <div className="p-6 border-b border-border text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center">
                <UserCircle className="h-8 w-8 text-primary" />
              </div>
              <Dialog.Title className="text-xl font-semibold">Choose Your Screen Name</Dialog.Title>
              <Dialog.Description className="text-muted-foreground mt-2">
                This name will be shown when you upload songs to the shared library.
              </Dialog.Description>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="space-y-2">
                <Label htmlFor="screen-name">Screen Name</Label>
                <Input
                  id="screen-name"
                  placeholder="Enter your screen name..."
                  value={screenNameInput}
                  onChange={(e) => {
                    setScreenNameInput(e.target.value);
                    setScreenNameError('');
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleSaveScreenName();
                    }
                  }}
                  maxLength={30}
                  className="text-lg"
                />
                <p className="text-xs text-muted-foreground">
                  2-30 characters. This will be visible to other users.
                </p>
                {screenNameError && (
                  <p className="text-sm text-destructive">{screenNameError}</p>
                )}
              </div>
            </div>
            
            <div className="p-6 border-t border-border">
              <Button
                onClick={handleSaveScreenName}
                disabled={savingScreenName || !screenNameInput.trim()}
                className="w-full bg-gradient-to-r from-primary to-purple-500 hover:from-primary/90 hover:to-purple-500/90"
              >
                {savingScreenName ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Continue'
                )}
              </Button>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </div>
  );
}
